import React, { useState, useRef } from 'react';
import {
  Upload, FileText, Image, Trash2, Loader, CheckCircle,
  File, Download, Eye,
} from 'lucide-react';
import { extractReceipt } from '../utils/extractReceipt';
import { currencySymbol } from '../utils/finance';
import { fmtDate } from '../utils/dateFormat';

const FILE_ICONS = {
  'application/pdf': FileText,
  'image/jpeg': Image,
  'image/png': Image,
  'image/webp': Image,
};

export default function DocumentUpload({
  business,
  isOwner,
  onUpload,
  onDocumentsChange,
  onAddClaim,
}) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractResult, setExtractResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef();

  const documents = business.documents || [];

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (!isOwner) {
      console.warn('[DocumentUpload] Upload blocked — isOwner=false. Check business.roles for your uid.');
      alert('Upload blocked: you do not have owner/editor permission on this business.');
      return;
    }

    // Single image → keep AI receipt extraction flow
    if (files.length === 1 && files[0].type.startsWith('image/')) {
      const file = files[0];
      setSelectedFile(file);
      setExtracting(true);
      try {
        const result = await extractReceipt(file);
        setExtractResult(result);
      } catch (err) {
        console.error('Extraction failed:', err);
        setExtractResult(null);
      }
      setExtracting(false);

      setUploading(true);
      try {
        await onUpload(business.id, file, { category: 'Uncategorized' });
      } catch (err) {
        console.error('Upload failed:', err);
      }
      setUploading(false);
      return;
    }

    // Multi-file / PDF upload — sequential, no extraction
    setUploading(true);
    for (const file of files) {
      try {
        await onUpload(business.id, file, { category: 'Uncategorized' });
      } catch (err) {
        console.error(`Upload failed for ${file.name}:`, err);
      }
    }
    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    setDragOver(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear drag state when cursor leaves the upload-zone itself,
    // not when moving between child elements
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver(false);
  };

  const createClaimFromExtraction = () => {
    if (!extractResult) return;

    onAddClaim({
      description: extractResult.vendor || 'Receipt upload',
      amount: extractResult.amount || 0,
      currency: extractResult.currency || (business.pettyCash?.currency || 'SGD'),
      category: extractResult.category || 'Other',
      fromAccount: '',
      date: extractResult.date || new Date().toISOString().slice(0, 10),
    });

    setExtractResult(null);
    setSelectedFile(null);
  };

  const deleteDocument = (docId) => {
    onDocumentsChange(documents.filter((d) => d.id !== docId));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <div className="document-upload">
      {/* Upload zone */}
      {isOwner && (
        <div
          className={`upload-zone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            accept="image/*,.pdf"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
          {uploading ? (
            <>
              <Loader size={32} className="spin" />
              <p>Uploading...</p>
            </>
          ) : extracting ? (
            <>
              <Loader size={32} className="spin" />
              <p>Analyzing receipt with AI...</p>
            </>
          ) : (
            <>
              <Upload size={32} />
              <p>Drop a receipt or document here, or click to browse</p>
              <span className="upload-hint">Images will be auto-analyzed to extract amount, vendor, and category</span>
            </>
          )}
        </div>
      )}

      {/* Extraction result */}
      {extractResult && (
        <div className="extract-result">
          <div className="extract-header">
            <CheckCircle size={18} className="extract-success-icon" />
            <span>Receipt data extracted</span>
          </div>
          <div className="extract-details">
            <div className="extract-row">
              <span className="extract-label">Vendor</span>
              <span className="extract-value">{extractResult.vendor || 'Unknown'}</span>
            </div>
            <div className="extract-row">
              <span className="extract-label">Amount</span>
              <span className="extract-value mono">
                {currencySymbol(extractResult.currency)}{extractResult.amount?.toLocaleString()}
              </span>
            </div>
            <div className="extract-row">
              <span className="extract-label">Category</span>
              <span className="extract-value">{extractResult.category || 'Other'}</span>
            </div>
            <div className="extract-row">
              <span className="extract-label">Date</span>
              <span className="extract-value mono">{extractResult.date || 'N/A'}</span>
            </div>
          </div>
          <div className="extract-actions">
            <button className="btn btn-primary" onClick={createClaimFromExtraction}>
              <CheckCircle size={16} /> Create Claim from Receipt
            </button>
            <button className="btn btn-ghost" onClick={() => { setExtractResult(null); setSelectedFile(null); }}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Document list */}
      <div className="document-list">
        <h3 className="doc-list-title">
          <FileText size={18} /> Documents ({documents.length})
        </h3>

        {documents.length === 0 ? (
          <div className="empty-state" style={{ padding: '30px 20px' }}>
            <File size={32} />
            <p>No documents uploaded yet.</p>
          </div>
        ) : (
          <div className="doc-items">
            {documents.map((doc) => {
              const IconComponent = FILE_ICONS[doc.type] || File;
              return (
                <div key={doc.id} className="doc-item">
                  <a
                    className="doc-item-left"
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open document in new tab"
                  >
                    <IconComponent size={20} className="doc-icon" />
                    <div>
                      <span className="doc-name">{doc.name}</span>
                      <span className="doc-meta">
                        {formatSize(doc.size)} · {fmtDate(doc.uploadedAt)}
                      </span>
                    </div>
                  </a>
                  <div className="doc-item-actions">
                    {doc.category && (
                      <span className="doc-category-badge">{doc.category}</span>
                    )}
                    <a
                      className="btn btn-ghost btn-sm"
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="View"
                    >
                      <Eye size={14} />
                    </a>
                    {isOwner && (
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => deleteDocument(doc.id)}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
